/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  RadioGroup,
  Radio,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { ProgressBar } from "@components/ProgressBar";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { usePostValidateNextStageMutation } from "@api/applications";
import {
  useLazyGetLoanSocialMediaQuery,
  usePatchSocialMediaDataMutation,
  usePostSocialMediaMutation,
} from "@api/socialMediaApi";
import { WEP_PAGE } from "@utils/expreciones";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { setLoanStatus } from "@store/slices/registerSlice";
import { ApiError } from "@interfaces/index";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import useStatusConfig from "@hooks/useStatusConfig";
import useTofuSteps from "@hooks/useTofuSteps";

const SCREEN_STATUS = "CSPDI";

const PresenciaDigitalPm = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const interfaceConfig = useInterfaceConfig();
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);

  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getSocialMedia] = useLazyGetLoanSocialMediaQuery();
  const [postSocialMedia] = usePostSocialMediaMutation();
  const [patchSocialMedia] = usePatchSocialMediaDataMutation();

  const application = useSelector((state: RootState) => state.register.application);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [digitalPresenceId, setDigitalPresenceId] = useState<string>("");

  const formik = useFormik({
    initialValues: {
      has_web_page: false,
      web_page: "",
      has_social_media: false,
      linkedin: "",
      facebook: "",
      instagram: "",
      twitter: "",
    },
    validationSchema: yup.object().shape({
      has_web_page: yup.boolean().required("Campo requerido"),
      web_page: yup.string().when("has_web_page", {
        is: true,
        then: (schema) => schema.required("Campo requerido").matches(WEP_PAGE, "URL no válida"),
      }),
      has_social_media: yup.boolean().required("Campo requerido"),
      linkedin: yup
        .string()
        .matches(WEP_PAGE, "URL no válida")
        .test("at_least_one", "Debes completar al menos una red social con una URL válida", function () {
          const { has_social_media, linkedin, facebook, instagram, twitter } = this.parent;
          if (has_social_media) {
            return linkedin || facebook || instagram || twitter ? true : false;
          }
          return true;
        }),
      facebook: yup
        .string()
        .matches(WEP_PAGE, "URL no válida")
        .test("at_least_one", "Debes completar al menos una red social con una URL válida", function () {
          const { has_social_media, linkedin, facebook, instagram, twitter } = this.parent;
          if (has_social_media) {
            return linkedin || facebook || instagram || twitter ? true : false;
          }
          return true;
        }),
      instagram: yup
        .string()
        .matches(WEP_PAGE, "URL no válida")
        .test("at_least_one", "Debes completar al menos una red social con una URL válida", function () {
          const { has_social_media, linkedin, facebook, instagram, twitter } = this.parent;
          if (has_social_media) {
            return linkedin || facebook || instagram || twitter ? true : false;
          }
          return true;
        }),
      twitter: yup
        .string()
        .matches(WEP_PAGE, "URL no válida")
        .test("at_least_one", "Debes completar al menos una red social con una URL válida", function () {
          const { has_social_media, linkedin, facebook, instagram, twitter } = this.parent;
          if (has_social_media) {
            return linkedin || facebook || instagram || twitter ? true : false;
          }
          return true;
        }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      try {
        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        setSubmitting(true);
        const data = {
          has_web_page: values.has_web_page ? true : false,
          web_page: values.web_page || "",
          has_social_media: values.has_social_media ? true : false,
          linkedin: values.linkedin || "",
          facebook: values.facebook || "",
          instagram: values.instagram || "",
          x_twitter: values.twitter || "",
        };

        if (digitalPresenceId) {
          await patchSocialMedia({
            client_details_id: clientDetails.id,
            social_media_id: digitalPresenceId || "",
            data,
          }).unwrap();
        } else {
          await postSocialMedia({ client_details_id: clientDetails.id, data }).unwrap();
        }

        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        dispatch(setLoanStatus(nextStatus.code));

        navigate(nextStatus.url);
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "PresenciaDigitalPm",
            values: formik.values,
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const social_media = await getSocialMedia(clientDetails.id).unwrap();
        if (social_media && social_media.id) {
          setDigitalPresenceId(social_media.id);

          formik.setValues({
            has_web_page: social_media.has_web_page || false,
            web_page: social_media.web_page,
            has_social_media: social_media.has_social_media || false,
            linkedin: social_media.linkedin,
            facebook: social_media.facebook,
            instagram: social_media.instagram,
            twitter: social_media.x_twitter,
          });

          setTimeout(() => {
            formik.validateForm();
          }, 200);
        }
      } catch (error) {
        console.error("Ha ocurrido un error al obtener la información de las redes sociales", error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <Box
          sx={{
            mt: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            {interfaceConfig.textos_tofu?.pm?.presencia_digital_pm?.titulo || "Presencia digital"}
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          <Box maxWidth={"sm"} mt={isMobileOrTablet ? 2 : 3}>
            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              textAlign={"center"}
              color={theme?.palette?.primary?.dark}
            >
              Conocer la información de los canales digitales de tu negocio nos permite mejorar tu oferta personalizada
            </Typography>
          </Box>

          <Container maxWidth="sm" sx={{ mt: 2, mb: 2 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Box my={2}>
                <FormControl fullWidth>
                  <Typography variant={isMobileOrTablet ? "body2" : "body1"} color={theme?.palette?.primary?.dark}>
                    ¿La empresa cuenta con página web?
                  </Typography>
                  <RadioGroup
                    row
                    aria-labelledby="has_web_page"
                    name="has_web_page"
                    sx={{ marginTop: "1em" }}
                    value={formik.values.has_web_page?.toString()}
                    onBlur={formik.handleBlur}
                    onChange={(event) => {
                      const newEvent = {
                        target: {
                          name: "has_web_page",
                          value: event.target.value === "true" ? true : false,
                        },
                      };
                      formik.handleChange(newEvent);
                      if (newEvent.target.value === false) {
                        formik.setFieldValue("web_page", "");
                      }
                      setTimeout(() => {
                        formik.validateForm();
                      }, 200);
                    }}
                  >
                    <Grid container>
                      <Grid item xs={4} md={4}>
                        <FormControlLabel
                          value={"true"}
                          control={<Radio />}
                          label="Si"
                          disabled={formik.isSubmitting || isLoading}
                        />
                      </Grid>

                      <Grid item xs={4} md={4}>
                        <FormControlLabel
                          value={"false"}
                          control={<Radio />}
                          label="No"
                          disabled={formik.isSubmitting || isLoading}
                        />
                      </Grid>
                    </Grid>
                  </RadioGroup>
                </FormControl>
              </Box>

              {formik.values.has_web_page && (
                <TextField
                  id="web_page"
                  name="web_page"
                  label="Página web"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.web_page && Boolean(formik.errors.web_page)}
                  helperText={formik.touched.web_page && formik.errors.web_page}
                  value={formik.values.web_page}
                  disabled={formik.isSubmitting || isLoading}
                  inputProps={{ maxLength: 255 }}
                />
              )}

              <Box my={2}>
                <FormControl fullWidth>
                  <Typography variant={isMobileOrTablet ? "body2" : "body1"} color={theme?.palette?.primary?.dark}>
                    ¿La empresa cuenta con redes sociales?
                  </Typography>
                  <RadioGroup
                    row
                    aria-labelledby="has_social_media"
                    name="has_social_media"
                    sx={{ marginTop: "1em" }}
                    value={formik.values.has_social_media?.toString()}
                    onBlur={formik.handleBlur}
                    onChange={(event) => {
                      const newEvent = {
                        target: {
                          name: "has_social_media",
                          value: event.target.value === "true" ? true : false,
                        },
                      };
                      formik.handleChange(newEvent);
                      if (newEvent.target.value === false) {
                        formik.setFieldValue("linkedin", "");
                        formik.setFieldValue("facebook", "");
                        formik.setFieldValue("instagram", "");
                        formik.setFieldValue("twitter", "");
                      }
                      setTimeout(() => {
                        formik.validateForm();
                      }, 200);
                    }}
                  >
                    <Grid container>
                      <Grid item xs={4} md={4}>
                        <FormControlLabel
                          value={"true"}
                          control={<Radio />}
                          label="Si"
                          disabled={formik.isSubmitting || isLoading}
                        />
                      </Grid>

                      <Grid item xs={4} md={4}>
                        <FormControlLabel
                          value={"false"}
                          control={<Radio />}
                          label="No"
                          disabled={formik.isSubmitting || isLoading}
                        />
                      </Grid>
                    </Grid>
                  </RadioGroup>
                </FormControl>
              </Box>

              {formik.values.has_social_media && (
                <Box>
                  <FormControl fullWidth>
                    <TextField
                      id="linkedin"
                      name="linkedin"
                      label="LinkedIn"
                      margin="normal"
                      fullWidth
                      autoComplete="off"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.linkedin && Boolean(formik.errors.linkedin)}
                      helperText={formik.touched.linkedin && formik.errors.linkedin}
                      value={formik.values.linkedin}
                      disabled={formik.isSubmitting || isLoading}
                      inputProps={{ maxLength: 255 }}
                    />
                  </FormControl>

                  <FormControl fullWidth>
                    <TextField
                      id="facebook"
                      name="facebook"
                      label="Facebook"
                      margin="normal"
                      fullWidth
                      autoComplete="off"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.facebook && Boolean(formik.errors.facebook)}
                      helperText={formik.touched.facebook && formik.errors.facebook}
                      value={formik.values.facebook}
                      disabled={formik.isSubmitting || isLoading}
                      inputProps={{ maxLength: 255 }}
                    />
                  </FormControl>

                  <FormControl fullWidth>
                    <TextField
                      id="instagram"
                      name="instagram"
                      label="Instagram"
                      margin="normal"
                      fullWidth
                      autoComplete="off"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.instagram && Boolean(formik.errors.instagram)}
                      helperText={formik.touched.instagram && formik.errors.instagram}
                      value={formik.values.instagram}
                      disabled={formik.isSubmitting || isLoading}
                      inputProps={{ maxLength: 255 }}
                    />
                  </FormControl>

                  <FormControl fullWidth>
                    <TextField
                      id="twitter"
                      name="twitter"
                      label="X (Twitter)"
                      margin="normal"
                      fullWidth
                      autoComplete="off"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.twitter && Boolean(formik.errors.twitter)}
                      helperText={formik.touched.twitter && formik.errors.twitter}
                      value={formik.values.twitter}
                      disabled={formik.isSubmitting || isLoading}
                      inputProps={{ maxLength: 255 }}
                    />
                  </FormControl>
                </Box>
              )}

              <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
                <Button type="submit" variant="default" disabled={!formik.isValid || formik.isSubmitting || isLoading}>
                  Continuar
                </Button>
              </Box>
            </form>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default PresenciaDigitalPm;
